import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocalization } from '../../hooks/useLocalization';
import { getSupplementaryFlights, getOfferDetails } from '../../services/tourvisioService';
import { getOfferDetails as getOfferBrandedFares } from '../../services/bookingService';

// ==========================================
// TYPES
// ==========================================
interface FlightOption {
    id: string;
    offerId: string;
    direction: 'outbound' | 'inbound';
    flightNumber: string;
    airline: {
        name: string;
        code: string;
        logo: string;
    };
    departure: {
        airport: string;
        code: string;
        time: string;
        date: string;
    };
    arrival: {
        airport: string;
        code: string;
        time: string;
        date: string;
    };
    duration: string;
    stops: number;
    priceDifference: number;
    price?: number;
    isDefault: boolean;
    isAvailable: boolean;
    baggage?: string;
    baggageKg?: number;
    segments?: any[];
    priceType?: 'package' | 'flight';
    brandedFareId?: string;
}

interface TourDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    offerId: string;
    searchId?: string;
    currentOutbound?: FlightOption;
    currentInbound?: FlightOption;
    onSelectFlights: (outbound: FlightOption | null, inbound: FlightOption | null) => void;
    basePrice: number;
    currency: string;
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================
const parseBaggageToKg = (baggageStr?: any): number => {
    if (!baggageStr) return 0; // No baggage info — return 0 (unknown)
    const str = String(baggageStr);
    const match = str.match(/(\d+)\s*(kg|pc)/i);
    if (match) {
        const value = parseInt(match[1]);
        return match[2].toLowerCase() === 'pc' ? value * 23 : value;
    }
    return 0;
};

// Check if a value contains FAKE (case-insensitive, type-safe)
const isFake = (val: any): boolean => {
    if (!val) return false;
    return String(val).toUpperCase().includes('FAKE');
};

// Format time for display — extract HH:mm from ISO or time strings
const formatTimeDisplay = (timeStr: any): string => {
    if (!timeStr) return '';
    const str = String(timeStr);
    // ISO format: 2026-06-18T17:05:00
    if (str.includes('T')) {
        const timePart = str.split('T')[1];
        if (timePart) return timePart.substring(0, 5); // HH:mm
    }
    // Already HH:mm or HH:mm:ss
    const match = str.match(/(\d{1,2}:\d{2})/);
    if (match) return match[1];
    return str;
};

// Helper function moved to component to access 't' or keep generic. 
// If we keep it here, we return a key or English default.
const getAirlineName = (airlineData: any): string => {
    if (!airlineData) return 'Charter';
    if (typeof airlineData === 'string') return airlineData;
    if (typeof airlineData === 'object' && airlineData.name) {
        return typeof airlineData.name === 'string' ? airlineData.name : 'Charter';
    }
    return 'Charter';
};

// ==========================================
// COLLAPSIBLE FILTER SECTION
// ==========================================
const FilterSection: React.FC<{
    title: string;
    icon: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}> = ({ title, icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border-b border-cyan-100 dark:border-cyan-900/30 last:border-b-0">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between py-2 px-1 text-left hover:bg-cyan-50/50 dark:hover:bg-cyan-900/20 transition-colors"
            >
                <span className="flex items-center gap-2 text-xs font-medium text-cyan-700 dark:text-cyan-400">
                    <span>{icon}</span> {title}
                </span>
                <svg
                    className={`w-3 h-3 text-cyan-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpen && (
                <div className="pb-3 px-1 animate-fadeIn">
                    {children}
                </div>
            )}
        </div>
    );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
export const TourDetailsModal: React.FC<TourDetailsModalProps> = ({
    isOpen,
    onClose,
    offerId,
    searchId,
    currentOutbound,
    currentInbound,
    onSelectFlights,
    basePrice,
    currency: initialCurrency
}) => {
    const { t, language } = useLocalization();
    const abortControllerRef = React.useRef<AbortController | null>(null);
    const basePriceRef = React.useRef(basePrice);
    const currencyRef = React.useRef(initialCurrency);

    useEffect(() => {
        basePriceRef.current = basePrice;
        currencyRef.current = initialCurrency;
    }, [basePrice, initialCurrency]);

    // Fallback to sessionStorage if searchId prop is not provided
    const effectiveSearchId = searchId || (typeof window !== 'undefined' ? sessionStorage.getItem('tourvisio_search_id') : null) || '';


    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [outboundFlights, setOutboundFlights] = useState<FlightOption[]>([]);
    const [inboundFlights, setInboundFlights] = useState<FlightOption[]>([]);
    const [selectedOutbound, setSelectedOutbound] = useState<FlightOption | null>(currentOutbound || null);
    const [selectedInbound, setSelectedInbound] = useState<FlightOption | null>(currentInbound || null);
    const [activeTab, setActiveTab] = useState<'outbound' | 'inbound'>('outbound');
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Branded fares for baggage upgrade
    const [brandedFares, setBrandedFares] = useState<{ outbound: any[]; return: any[] }>({ outbound: [], return: [] });
    const [brandedFaresLoading, setBrandedFaresLoading] = useState(false);
    const [selectedBrandedFare, setSelectedBrandedFare] = useState<{ outbound: string | null; return: string | null }>({ outbound: null, return: null });
    const brandedFareCache = useRef<Record<string, { outbound: any[]; return: any[] }>>({});

    // Filters
    const [filters, setFilters] = useState({
        stopCount: [] as number[],
        airlines: [] as string[],
        minBaggage: 0,
        maxPrice: 999999
    });

    // Extract unique values for filters
    const filterOptions = useMemo(() => {
        const allFlights = [...outboundFlights, ...inboundFlights];
        // Compute unique baggage kg values from actual flight data
        const baggageKgValues = [...new Set(allFlights.map(f => f.baggageKg || 0).filter(v => v > 0))].sort((a, b) => a - b);
        return {
            airlines: [...new Set(allFlights.map(f => f.airline?.name).filter(Boolean))],
            stopsData: {
                0: allFlights.filter(f => (f.stops ?? 0) === 0).length,
                1: allFlights.filter(f => (f.stops ?? 0) === 1).length,
                2: allFlights.filter(f => (f.stops ?? 0) >= 2).length
            },
            maxPrice: Math.max(...allFlights.map(f => f.price || basePrice + 10000), basePrice + 10000),
            baggageOptions: baggageKgValues
        };
    }, [outboundFlights, inboundFlights, basePrice]);

    // Fetch flights from offer details (works without searchId)
    useEffect(() => {
        if (!isOpen) return;

        // Abort any previous fetch
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const signal = controller.signal;

        const fetchFlights = async () => {
            setLoading(true);
            setError(null);

            try {
                // First try to get from offer details (more reliable)
                if (offerId) {
                    const offerDetails = await getOfferDetails(offerId, (log) => console.log('Offer Details:', log));

                    if (offerDetails) {
                        const flights: FlightOption[] = [];

                        if (offerDetails.outboundFlight) {
                            flights.push({
                                id: `outbound-${offerId}`,
                                offerId: offerId,
                                direction: 'outbound',
                                flightNumber: String(offerDetails.outboundFlight.flightNumber || t('charter') || 'Charter'),
                                airline: {
                                    name: getAirlineName(offerDetails.outboundFlight.airline),
                                    code: '',
                                    logo: offerDetails.outboundFlight.logoUrl || ''
                                },
                                departure: {
                                    airport: String(offerDetails.outboundFlight.departureAirport || ''),
                                    code: String(offerDetails.outboundFlight.departureAirport || ''),
                                    time: String(offerDetails.outboundFlight.departureTime || ''),
                                    date: ''
                                },
                                arrival: {
                                    airport: String(offerDetails.outboundFlight.arrivalAirport || ''),
                                    code: String(offerDetails.outboundFlight.arrivalAirport || ''),
                                    time: String(offerDetails.outboundFlight.arrivalTime || ''),
                                    date: ''
                                },
                                duration: String(offerDetails.outboundFlight.duration || ''),
                                stops: offerDetails.outboundFlight.stops ?? 0,
                                priceDifference: 0,
                                price: basePriceRef.current,
                                isDefault: true,
                                isAvailable: true,
                                baggage: String(offerDetails.outboundFlight.baggage || offerDetails.outboundFlight.cabinClass || ''),
                                baggageKg: parseBaggageToKg(offerDetails.outboundFlight.baggage),
                                segments: offerDetails.outboundFlight.segments
                            });
                        }

                        if (offerDetails.returnFlight) {
                            flights.push({
                                id: `inbound-${offerId}`,
                                offerId: offerId,
                                direction: 'inbound',
                                flightNumber: String(offerDetails.returnFlight.flightNumber || t('charter') || 'Charter'),
                                airline: {
                                    name: getAirlineName(offerDetails.returnFlight.airline),
                                    code: '',
                                    logo: offerDetails.returnFlight.logoUrl || ''
                                },
                                departure: {
                                    airport: String(offerDetails.returnFlight.departureAirport || ''),
                                    code: String(offerDetails.returnFlight.departureAirport || ''),
                                    time: String(offerDetails.returnFlight.departureTime || ''),
                                    date: ''
                                },
                                arrival: {
                                    airport: String(offerDetails.returnFlight.arrivalAirport || ''),
                                    code: String(offerDetails.returnFlight.arrivalAirport || ''),
                                    time: String(offerDetails.returnFlight.arrivalTime || ''),
                                    date: ''
                                },
                                duration: String(offerDetails.returnFlight.duration || ''),
                                stops: offerDetails.returnFlight.stops ?? 0,
                                priceDifference: 0,
                                price: basePriceRef.current,
                                isDefault: true,
                                isAvailable: true,
                                baggage: String(offerDetails.returnFlight.baggage || offerDetails.returnFlight.cabinClass || ''),
                                baggageKg: parseBaggageToKg(offerDetails.returnFlight.baggage),
                                segments: offerDetails.returnFlight.segments
                            });
                        }

                        if (flights.length > 0) {
                            setOutboundFlights(flights.filter(f => f.direction === 'outbound'));
                            setInboundFlights(flights.filter(f => f.direction === 'inbound'));
                            setSelectedOutbound(flights.find(f => f.direction === 'outbound') || null);
                            setSelectedInbound(flights.find(f => f.direction === 'inbound') || null);
                        }
                    }
                }

                // Try supplementary flights if we have searchId
                if (effectiveSearchId && effectiveSearchId !== 'NO_SEARCH_ID_PROVIDED') {
                    // Fetch all available flights (page size 100)
                    const culture = language === 'sv' ? 'sv-SE' : 'en-US';
                    const result = await getSupplementaryFlights(effectiveSearchId, 1, 100, currencyRef.current, culture, signal);
                    if (result.success && result.flights.length > 0) {
                        // Filter out FAKE flights and flights with empty essential data
                        const cleanFlights = result.flights.filter((f: any) => {
                            if (isFake(f.flightNumber) || isFake(f.offerId) || isFake(f.id) || isFake(f.airline)) return false;
                            // Skip flights with no departure/arrival info
                            if (!f.departureAirport && !f.arrivalAirport && !f.flightNumber) return false;
                            return true;
                        });

                        const mapFlight = (f: any, dir: 'outbound' | 'inbound'): FlightOption => {
                            const rawPrice = (f.price != null && !isNaN(parseFloat(String(f.price)))) ? parseFloat(String(f.price)) : null;
                            const pt: 'package' | 'flight' = f.priceType || 'package';

                            // For 'package' priceType: price is total package → delta vs basePrice
                            // For 'flight' priceType: price is flight-only → delta will be recomputed below
                            let priceDiff = 0;
                            if (rawPrice != null) {
                                if (pt === 'package') {
                                    priceDiff = rawPrice - basePriceRef.current;
                                }
                                // For 'flight', priceDiff = 0 initially; will be fixed in fixFlightTypeDeltas
                            }

                            console.log(`📊 mapFlight: ${f.flightNumber} ${dir}, priceType=${pt}, rawPrice=${rawPrice}, basePrice=${basePriceRef.current}, delta=${priceDiff}`);

                            return {
                                id: String(f.offerId || '') || `${dir}-${Math.random().toString(36).substr(2, 9)}`,
                                offerId: String(f.offerId || ''),
                                direction: dir,
                                flightNumber: String(f.flightNumber || t('charter') || 'Charter'),
                                airline: { name: getAirlineName(f.airline), code: '', logo: f.logoUrl || '' },
                                departure: { airport: String(f.departureAirport || ''), code: String(f.departureAirport || ''), time: String(f.departureTime || ''), date: '' },
                                arrival: { airport: String(f.arrivalAirport || ''), code: String(f.arrivalAirport || ''), time: String(f.arrivalTime || ''), date: '' },
                                duration: String(f.duration || ''),
                                stops: f.stops ?? 0,
                                priceDifference: priceDiff,
                                price: rawPrice ?? basePriceRef.current,
                                isDefault: false,
                                isAvailable: true,
                                baggage: String(f.baggage || ''),
                                baggageKg: parseBaggageToKg(f.baggage),
                                segments: f.segments,
                                priceType: pt
                            };
                        };

                        // Recompute deltas for flight-type prices: compare vs cheapest flight in the group
                        const fixFlightTypeDeltas = (flights: FlightOption[]): FlightOption[] => {
                            const flightTypePrices = flights
                                .filter(f => f.priceType === 'flight' && f.price != null && f.price > 0)
                                .map(f => f.price!);
                            if (flightTypePrices.length === 0) return flights;

                            const minFlightPrice = Math.min(...flightTypePrices);
                            console.log(`💰 fixFlightTypeDeltas: ${flights.length} flights, minFlightPrice=${minFlightPrice}, prices=${JSON.stringify(flightTypePrices)}`);

                            return flights.map(f => {
                                if (f.priceType === 'flight' && f.price != null && f.price > 0) {
                                    return { ...f, priceDifference: Math.round(f.price - minFlightPrice) };
                                }
                                return f;
                            });
                        };

                        let newOutbound = cleanFlights.filter((f: any) => f.direction === 'outbound').map((f: any) => mapFlight(f, 'outbound'));
                        let newInbound = cleanFlights.filter((f: any) => f.direction === 'return' || f.direction === 'inbound').map((f: any) => mapFlight(f, 'inbound'));

                        // Fix flight-type deltas (PT3 flights compared to cheapest within direction)
                        newOutbound = fixFlightTypeDeltas(newOutbound);
                        newInbound = fixFlightTypeDeltas(newInbound);

                        // Filter out flights with undefined/NaN prices (user requirement #3)
                        newOutbound = newOutbound.filter(f => f.price != null && !isNaN(f.price));
                        newInbound = newInbound.filter(f => f.price != null && !isNaN(f.price));

                        if (newOutbound.length > 0) setOutboundFlights(prev => [...prev, ...newOutbound]);
                        if (newInbound.length > 0) setInboundFlights(prev => [...prev, ...newInbound]);
                    }
                }

            } catch (err: any) {
                if (err?.name === 'AbortError' || signal.aborted) {
                    console.log('🛑 Flight fetch aborted (modal closed or selection made)');
                    return; // Don't set error state for intentional aborts
                }
                console.error('Error fetching flights:', err);
                setError(t('error_loading_flights'));
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchFlights();

        // Cleanup: abort on unmount or when deps change
        return () => {
            controller.abort();
        };
    }, [isOpen, offerId, effectiveSearchId]);

    // Apply filters
    const filteredFlights = useMemo(() => {
        const filterFn = (flight: FlightOption): boolean => {
            if (filters.stopCount.length > 0 && !filters.stopCount.includes(Math.min(flight.stops ?? 0, 2))) return false;
            if (filters.airlines.length > 0 && !filters.airlines.includes(flight.airline?.name || '')) return false;
            if (filters.minBaggage > 0 && (flight.baggageKg || 0) < filters.minBaggage) return false;
            if ((flight.price || 0) > filters.maxPrice) return false;
            return true;
        };

        return {
            outbound: outboundFlights.filter(filterFn),
            inbound: inboundFlights.filter(filterFn)
        };
    }, [outboundFlights, inboundFlights, filters]);

    // Clear filters
    const handleClearFilters = useCallback(() => {
        setFilters({
            stopCount: [],
            airlines: [],
            minBaggage: 0,
            maxPrice: 999999
        });
    }, []);

    // Toggle stop count
    const toggleStopCount = (count: number) => {
        setFilters(prev => ({
            ...prev,
            stopCount: prev.stopCount.includes(count)
                ? prev.stopCount.filter(c => c !== count)
                : [...prev.stopCount, count]
        }));
    };

    // Fetch branded fares when a 0-baggage flight is selected
    const fetchBrandedFares = useCallback(async (flight: FlightOption, direction: 'outbound' | 'return') => {
        if (!flight?.offerId || (flight.baggageKg || 0) > 0) {
            // Has baggage — clear branded fares for this direction
            setBrandedFares(prev => ({ ...prev, [direction]: [] }));
            setSelectedBrandedFare(prev => ({ ...prev, [direction]: null }));
            return;
        }

        // Check cache first
        const cacheKey = `${flight.offerId}_${direction}`;
        if (brandedFareCache.current[cacheKey]) {
            const cached = brandedFareCache.current[cacheKey];
            setBrandedFares(prev => ({ ...prev, [direction]: cached[direction] }));
            return;
        }

        setBrandedFaresLoading(true);
        try {
            const details = await getOfferBrandedFares(flight.offerId, currencyRef.current || 'SEK');
            const fares = {
                outbound: details.outboundBrandedFares || [],
                return: details.returnBrandedFares || []
            };
            brandedFareCache.current[cacheKey] = fares;
            setBrandedFares(prev => ({ ...prev, [direction]: fares[direction] }));
            console.log(`✈️ Branded fares for ${direction}:`, fares[direction].length, 'options');
        } catch (err) {
            console.error('Failed to load branded fares:', err);
        } finally {
            setBrandedFaresLoading(false);
        }
    }, [initialCurrency]);

    // Trigger branded fare fetch when selection changes
    useEffect(() => {
        if (selectedOutbound) {
            fetchBrandedFares(selectedOutbound, 'outbound');
        }
    }, [selectedOutbound?.offerId, fetchBrandedFares]);

    useEffect(() => {
        if (selectedInbound) {
            fetchBrandedFares(selectedInbound, 'return');
        }
    }, [selectedInbound?.offerId, fetchBrandedFares]);

    // Handle branded fare selection
    const handleBrandedFareSelect = (direction: 'outbound' | 'return', fareId: string) => {
        const isTogglingOff = selectedBrandedFare[direction] === fareId;
        setSelectedBrandedFare(prev => ({
            ...prev,
            [direction]: isTogglingOff ? null : fareId
        }));

        // Automatic progression if toggling ON
        if (!isTogglingOff) {
            setTimeout(() => {
                if (direction === 'outbound') {
                    setActiveTab('inbound');
                } else if (direction === 'return') {
                    // Check if outbound also needs baggage and isn't selected
                    if (selectedOutbound && (selectedOutbound.baggageKg || 0) === 0 && !selectedBrandedFare.outbound) {
                        setActiveTab('outbound');
                    } else if (selectedInbound) {
                        // Both satisfied, close and confirm
                        if (abortControllerRef.current) {
                            abortControllerRef.current.abort();
                            abortControllerRef.current = null;
                        }
                        
                        // We must pass the latest selected fare state to onSelectFlights if needed
                        const finalOutbound = selectedOutbound ? { ...selectedOutbound, brandedFareId: selectedBrandedFare.outbound || undefined } : null;
                        const finalInbound = selectedInbound ? { ...selectedInbound, brandedFareId: fareId } : null; // Because state update is async, use fareId directly
                        
                        onSelectFlights(finalOutbound, finalInbound);
                        onClose();
                    }
                }
            }, 300); // slight delay for visual feedback
        }
    };

    // Get selected branded fare price delta
    const brandedFareDelta = useMemo(() => {
        let delta = 0;
        const outFare = brandedFares.outbound.find((f: any) => f.id === selectedBrandedFare.outbound);
        if (outFare) delta += outFare.price || 0;
        const retFare = brandedFares.return.find((f: any) => f.id === selectedBrandedFare.return);
        if (retFare) delta += retFare.price || 0;
        return delta;
    }, [brandedFares, selectedBrandedFare]);

    // Confirm selection
    const handleConfirm = () => {
        // Abort any pending API requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        
        const finalOutbound = selectedOutbound ? { ...selectedOutbound, brandedFareId: selectedBrandedFare.outbound || undefined } : null;
        const finalInbound = selectedInbound ? { ...selectedInbound, brandedFareId: selectedBrandedFare.return || undefined } : null;
        
        onSelectFlights(finalOutbound, finalInbound);
        onClose();
    };

    // Select flight logic
    const handleQuickSelect = (flight: FlightOption) => {
        if (flight.direction === 'outbound') {
            setSelectedOutbound(flight);
            
            // If the flight has 0 baggage and no branded fare is selected yet, stay on this tab
            if ((flight.baggageKg || 0) === 0 && !selectedBrandedFare.outbound) {
                return;
            }
            
            // Otherwise move to the inbound tab
            setActiveTab('inbound');
        } else {
            setSelectedInbound(flight);
            
            // If the flight has 0 baggage and no branded fare is selected yet, stay on this tab
            if ((flight.baggageKg || 0) === 0 && !selectedBrandedFare.return) {
                return;
            }
            
            // If outbound needs baggage, go back to outbound
            if (selectedOutbound && (selectedOutbound.baggageKg || 0) === 0 && !selectedBrandedFare.outbound) {
                setActiveTab('outbound');
                return;
            }
            
            // Abort any pending API requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }

            // Both selected and baggage handled, close and confirm
            const finalOutbound = selectedOutbound ? { ...selectedOutbound, brandedFareId: selectedBrandedFare.outbound || undefined } : null;
            const finalInbound = { ...flight, brandedFareId: selectedBrandedFare.return || undefined };
            onSelectFlights(finalOutbound, finalInbound);
            onClose();
        }
    };

    // Price difference
    const totalPriceDifference = useMemo(() => {
        let diff = 0;
        if (selectedOutbound) diff += selectedOutbound.priceDifference || 0;
        if (selectedInbound) diff += selectedInbound.priceDifference || 0;
        return diff;
    }, [selectedOutbound, selectedInbound]);

    if (!isOpen) return null;

    const currentFlights = activeTab === 'outbound' ? filteredFlights.outbound : filteredFlights.inbound;
    const hasActiveFilters = filters.stopCount.length > 0 || filters.airlines.length > 0 || filters.minBaggage > 0;

    // Filter Panel Content
    const FilterPanel = () => (
        <div className="space-y-1">
            {/* Stop Count */}
            <FilterSection title={t('stops')} icon="✈️" defaultOpen>
                <div className="space-y-1">
                    {[
                        { value: 0, label: t('direct_flight'), count: filterOptions.stopsData[0] },
                        { value: 1, label: `1 ${t('stops')}`, count: filterOptions.stopsData[1] },
                        { value: 2, label: `2+ ${t('stops')}`, count: filterOptions.stopsData[2] }
                    ].map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-xs py-1 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded px-1">
                            <input
                                type="checkbox"
                                checked={filters.stopCount.includes(opt.value)}
                                onChange={() => toggleStopCount(opt.value)}
                                className="w-3.5 h-3.5 text-cyan-500 rounded border-cyan-300 focus:ring-cyan-500"
                            />
                            <span className="text-gray-700 dark:text-gray-300 flex-1">{opt.label}</span>
                            <span className="text-gray-400 text-[10px]">({opt.count})</span>
                        </label>
                    ))}
                </div>
            </FilterSection>

            {/* Airlines */}
            {filterOptions.airlines.length > 0 && (
                <FilterSection title={t('airline')} icon="🛩️">
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                        {filterOptions.airlines.map(airline => (
                            <label key={airline} className="flex items-center gap-2 cursor-pointer text-xs py-1 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded px-1">
                                <input
                                    type="checkbox"
                                    checked={filters.airlines.includes(airline)}
                                    onChange={() => setFilters(prev => ({
                                        ...prev,
                                        airlines: prev.airlines.includes(airline)
                                            ? prev.airlines.filter(a => a !== airline)
                                            : [...prev.airlines, airline]
                                    }))}
                                    className="w-3.5 h-3.5 text-cyan-500 rounded border-cyan-300 focus:ring-cyan-500"
                                />
                                <span className="text-gray-700 dark:text-gray-300 truncate">{airline}</span>
                            </label>
                        ))}
                    </div>
                </FilterSection>
            )}

            {/* Baggage */}
            <FilterSection title={t('min_baggage') || 'Min. bagage'} icon="🧳">
                <select
                    value={filters.minBaggage}
                    onChange={(e) => setFilters(prev => ({ ...prev, minBaggage: parseInt(e.target.value) }))}
                    className="w-full text-xs px-2 py-1.5 rounded border border-cyan-200 dark:border-cyan-800 bg-white dark:bg-gray-800 focus:ring-1 focus:ring-cyan-500"
                >
                    <option value={0}>{t('any')}</option>
                    {filterOptions.baggageOptions.map(kg => (
                        <option key={kg} value={kg}>{kg}+ kg</option>
                    ))}
                </select>
            </FilterSection>

            {/* Clear */}
            {hasActiveFilters && (
                <button
                    onClick={handleClearFilters}
                    className="w-full text-xs text-cyan-600 hover:text-cyan-700 py-2 hover:underline"
                >
                    {t('clear_filters')}
                </button>
            )}
        </div>
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-3 flex justify-between items-center flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold flex items-center gap-2">
                            ✈️ {t('tour_details')}
                        </h2>
                        <p className="text-cyan-100 text-xs">
                            {t('view_flight_options')}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-2xl font-bold leading-none p-1">×</button>
                </div>

                {/* Tabs + Mobile Filter Toggle */}
                <div className="flex items-center border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <button
                        onClick={() => setActiveTab('outbound')}
                        className={`flex-1 py-2.5 text-center text-sm font-medium transition-all ${activeTab === 'outbound'
                            ? 'text-cyan-600 border-b-2 border-cyan-500 bg-cyan-50/50 dark:bg-cyan-900/20'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        🛫 {t('outbound_flight')} ({filteredFlights.outbound.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('inbound')}
                        className={`flex-1 py-2.5 text-center text-sm font-medium transition-all ${activeTab === 'inbound'
                            ? 'text-cyan-600 border-b-2 border-cyan-500 bg-cyan-50/50 dark:bg-cyan-900/20'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        🛬 {t('inbound_flight')} ({filteredFlights.inbound.length})
                    </button>
                    {/* Mobile filter button */}
                    <button
                        onClick={() => setShowMobileFilters(!showMobileFilters)}
                        className={`md:hidden px-3 py-2.5 border-l border-gray-200 dark:border-gray-700 ${hasActiveFilters ? 'text-cyan-600' : 'text-gray-500'}`}
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        {hasActiveFilters && <span className="absolute -top-1 -right-1 w-2 h-2 bg-cyan-500 rounded-full"></span>}
                    </button>
                </div>

                {/* Mobile Filters Drawer */}
                {showMobileFilters && (
                    <div className="md:hidden bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 animate-fadeIn">
                        <FilterPanel />
                    </div>
                )}

                {/* Main Content */}
                <div className="flex flex-1 overflow-hidden">

                    {/* Desktop Sidebar Filters */}
                    <div className="hidden md:block w-48 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-3 overflow-y-auto flex-shrink-0">
                        <h3 className="text-xs font-bold text-cyan-600 uppercase tracking-wider mb-2">
                            {t('filters')}
                        </h3>
                        <FilterPanel />
                    </div>

                    {/* Flight List */}
                    <div className="flex-1 overflow-y-auto p-3 sm:p-4">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500 mb-3"></div>
                                <p className="text-gray-500 text-sm">{t('loading')}</p>
                            </div>
                        ) : error ? (
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center">
                                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                            </div>
                        ) : currentFlights.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <p className="text-3xl mb-3">✈️</p>
                                <p className="text-sm">{t('no_flights_found')}</p>
                                {hasActiveFilters && (
                                    <button onClick={handleClearFilters} className="mt-3 text-cyan-600 hover:underline text-sm">
                                        {t('clear_filters')}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {currentFlights.map((flight) => {
                                    const isSelected = activeTab === 'outbound'
                                        ? selectedOutbound?.id === flight.id
                                        : selectedInbound?.id === flight.id;

                                    return (
                                        <div
                                            key={flight.id}
                                            onClick={() => {
                                                if (activeTab === 'outbound') setSelectedOutbound(flight);
                                                else setSelectedInbound(flight);
                                            }}
                                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${isSelected
                                                ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 shadow-md'
                                                : 'border-gray-200 dark:border-gray-700 hover:border-cyan-300 hover:shadow-sm'
                                                }`}
                                        >
                                            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
                                                {/* Airline */}
                                                <div className="flex items-center gap-2 min-w-[100px]">
                                                    {flight.airline?.logo ? (
                                                        <img
                                                            src={flight.airline.logo}
                                                            alt={flight.airline.name}
                                                            className="w-8 h-8 object-contain"
                                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900 rounded-full flex items-center justify-center text-sm">✈️</div>
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="font-semibold text-gray-800 dark:text-white text-sm truncate">{flight.airline?.name || 'Charter'}</p>
                                                        <p className="text-[10px] text-gray-500">{flight.flightNumber}</p>
                                                    </div>
                                                </div>

                                                {/* Times */}
                                                <div className="flex items-center gap-2 flex-1 justify-center">
                                                    <div className="text-center">
                                                        <p className="text-base font-bold text-gray-800 dark:text-white">{formatTimeDisplay(flight.departure?.time) || '—'}</p>
                                                        <p className="text-[10px] text-gray-500">{flight.departure?.code}</p>
                                                    </div>
                                                    <div className="flex flex-col items-center min-w-[70px]">
                                                        <p className="text-[10px] text-gray-400">{flight.duration || '—'}</p>
                                                        <div className="w-full h-px bg-gray-300 dark:bg-gray-600 my-1 relative">
                                                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-l-[5px] border-l-gray-400 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent"></div>
                                                        </div>
                                                        <p className="text-[10px] text-gray-400">
                                                            {(flight.stops ?? 0) === 0 ? t('direct_flight') : `${flight.stops} ${t('stops')}`}
                                                        </p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-base font-bold text-gray-800 dark:text-white">{formatTimeDisplay(flight.arrival?.time) || '—'}</p>
                                                        <p className="text-[10px] text-gray-500">{flight.arrival?.code}</p>
                                                    </div>
                                                </div>

                                                {/* Baggage - PROMINENT */}
                                                <div className="flex items-center gap-1 px-2 py-1 bg-cyan-50 dark:bg-cyan-900/30 rounded text-xs">
                                                    <span>🧳</span>
                                                    <span className="font-semibold text-cyan-700 dark:text-cyan-400">
                                                        {flight.baggage || (flight.baggageKg ? `${flight.baggageKg} kg` : '—')}
                                                    </span>
                                                </div>

                                                {/* Price + Select */}
                                                <div className="text-right min-w-[80px] flex flex-col items-end gap-1">
                                                    {flight.priceDifference !== 0 ? (
                                                        <p className={`text-sm font-bold ${flight.priceDifference > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                                            {flight.priceDifference > 0 ? '+' : ''}{flight.priceDifference} {initialCurrency}
                                                        </p>
                                                    ) : (
                                                        <p className="text-gray-500 text-xs">{t('included')}</p>
                                                    )}
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleQuickSelect(flight); }}
                                                        className="px-3 py-1 text-xs font-semibold rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 transition-all shadow-sm hover:shadow-md"
                                                    >
                                                        {t('select')}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* SEGMENTS DISPLAY */}
                                            {flight.segments && flight.segments.length > 1 && (
                                                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800/50 ml-11 space-y-2 animate-fadeIn">
                                                    <p className="text-[10px] font-bold text-cyan-600 uppercase tracking-wider mb-1">
                                                        {t('flight_segments')}
                                                    </p>
                                                    {flight.segments.map((seg: any, sIdx: number) => {
                                                        // Safe extractors: seg fields may be strings or objects from raw API
                                                        const segAirline = typeof seg.airline === 'object' ? (seg.airline?.name || '') : (seg.airline || '');
                                                        const segDepAirport = typeof seg.departureAirport === 'object' ? (seg.departureAirport?.code || seg.departureAirport?.name || '') : (seg.departureAirport || seg.departure?.airport?.code || seg.departure?.name || '');
                                                        const segArrAirport = typeof seg.arrivalAirport === 'object' ? (seg.arrivalAirport?.code || seg.arrivalAirport?.name || '') : (seg.arrivalAirport || seg.arrival?.airport?.code || seg.arrival?.name || '');
                                                        const segDepTime = seg.departureTime || seg.departure?.date || '';
                                                        const segFlightNo = String(seg.flightNumber || seg.flightNo || '');

                                                        return (
                                                            <div key={sIdx} className="flex items-center gap-3 text-[10px]">
                                                                <div className="w-12 text-gray-400">{formatTimeDisplay(segDepTime)}</div>
                                                                <div className="flex-1 flex items-center gap-2">
                                                                    <span className="font-semibold text-gray-700 dark:text-gray-200">{segDepAirport}</span>
                                                                    <span className="text-gray-300">→</span>
                                                                    <span className="font-semibold text-gray-700 dark:text-gray-200">{segArrAirport}</span>
                                                                </div>
                                                                <div className="text-gray-400 truncate max-w-[100px]">{segAirline}{segFlightNo ? ` • ${segFlightNo}` : ''}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* BRANDED FARE UPGRADE SECTION — shown when selected flight has no baggage */}
                                            {isSelected && (flight.baggageKg || 0) === 0 && (
                                                <div className="mt-3 pt-3 border-t border-cyan-200 dark:border-cyan-800/50 animate-fadeIn">
                                                    {brandedFaresLoading ? (
                                                        <div className="flex items-center gap-2 py-3">
                                                            <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                                                            <span className="text-xs text-gray-500">{t('loading_fares')}</span>
                                                        </div>
                                                    ) : (brandedFares[activeTab === 'outbound' ? 'outbound' : 'return'] || []).length > 0 ? (
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-orange-500">⚠️</span>
                                                                <span className="text-xs font-semibold text-orange-700 dark:text-orange-400">
                                                                    {t('no_baggage_warning')}
                                                                </span>
                                                            </div>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                                                                {(brandedFares[activeTab === 'outbound' ? 'outbound' : 'return'] || []).map((fare: any) => {
                                                                    const dir = activeTab === 'outbound' ? 'outbound' : 'return' as 'outbound' | 'return';
                                                                    const isSelectedFare = selectedBrandedFare[dir] === fare.id;
                                                                    const hasBaggageFeature = fare.features?.some((f: any) =>
                                                                        f.included && (f.icon === 'luggage' || f.name?.toLowerCase().includes('bagaj') || f.name?.toLowerCase().includes('baggage') || f.name?.toLowerCase().includes('bag'))
                                                                    );
                                                                    return (
                                                                        <button
                                                                            key={fare.id}
                                                                            type="button"
                                                                            onClick={(e) => { e.stopPropagation(); handleBrandedFareSelect(dir, fare.id); }}
                                                                            className={`relative p-3 rounded-lg border-2 text-left transition-all duration-200 ${isSelectedFare
                                                                                ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/30 shadow-md ring-1 ring-cyan-200'
                                                                                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-cyan-300 hover:shadow-sm'
                                                                                }`}
                                                                        >
                                                                            {isSelectedFare && (
                                                                                <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-cyan-500 rounded-full flex items-center justify-center">
                                                                                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                                                </div>
                                                                            )}
                                                                            <h5 className="font-bold text-gray-900 dark:text-gray-100 text-xs mb-1.5">{fare.name}</h5>
                                                                            <div className="space-y-1 mb-2">
                                                                                {fare.features?.slice(0, 4).map((feature: any, idx: number) => (
                                                                                    <div key={idx} className="flex items-center gap-1.5">
                                                                                        <span className={`text-[10px] ${feature.included ? (feature.icon === 'luggage' ? 'text-cyan-500' : 'text-green-500') : 'text-gray-300'}`}>
                                                                                            {feature.included ? '✓' : '✗'}
                                                                                        </span>
                                                                                        <span className={`text-[10px] ${feature.included ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 line-through'} ${feature.included && feature.icon === 'luggage' ? 'font-semibold text-cyan-700 dark:text-cyan-400' : ''
                                                                                            }`}>
                                                                                            {feature.name}
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                                {(fare.features?.length || 0) > 4 && (
                                                                                    <span className="text-[10px] text-cyan-500">+{fare.features.length - 4} {t('more')}</span>
                                                                                )}
                                                                            </div>
                                                                            <div className="pt-1.5 border-t border-gray-100 dark:border-gray-700">
                                                                                {fare.price === 0 ? (
                                                                                    <span className="text-xs font-bold text-green-600">{t('included')}</span>
                                                                                ) : (
                                                                                    <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                                                                                        +{fare.price?.toLocaleString('sv-SE')} {fare.currency || initialCurrency}
                                                                                    </span>
                                                                                )}
                                                                                {hasBaggageFeature && (
                                                                                    <span className="ml-1.5 text-[9px] bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-400 px-1.5 py-0.5 rounded-full font-semibold">🧳 {t('baggage_label')}</span>
                                                                                )}
                                                                            </div>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 py-2">
                                                            <span className="text-orange-500">⚠️</span>
                                                            <span className="text-xs text-orange-600 dark:text-orange-400">
                                                                {t('no_baggage_info')}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-3 flex-shrink-0">
                    <div className="text-center sm:text-left">
                        <p className="text-xs text-gray-500">{t('total_difference')}</p>
                        <p className={`text-lg font-bold ${(totalPriceDifference + brandedFareDelta) > 0 ? 'text-red-500' : (totalPriceDifference + brandedFareDelta) < 0 ? 'text-green-500' : 'text-gray-700 dark:text-gray-300'}`}>
                            {(totalPriceDifference + brandedFareDelta) > 0 ? '+' : ''}{totalPriceDifference + brandedFareDelta} {initialCurrency}
                        </p>
                        {brandedFareDelta > 0 && (
                            <p className="text-[10px] text-cyan-600">({t('includes_fare_upgrade')}: +{brandedFareDelta} {initialCurrency})</p>
                        )}
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                        <button
                            onClick={onClose}
                            className="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                        >
                            {t('close')}
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={loading}
                            className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium hover:from-cyan-600 hover:to-blue-700 transition-all text-sm disabled:opacity-50"
                        >
                            {t('confirm')}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
            `}</style>
        </div>
    );
};

export default TourDetailsModal;
